import { useQuery } from "@tanstack/react-query";
import { portalApi } from "@/lib/portalApi";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CircleHelp, Upload, MessageSquare, FileText, CheckCircle2, Phone, Mail, Globe, MapPin } from "lucide-react";

// Firm contact details — fill in what should be shown; empty strings are hidden.
export const FIRM_CONTACT = {
  phone: "(770) 507-6625",
  fax: "(770) 507-6627",
  textNumber: "(770) 720-6250",
  email: "",          // fill in when Wade provides one
  website: "https://carpentercpas.com/",
  address: "356 Corporate Center Court, Stockbridge, GA 30281",
  hours: "Mon–Fri 8:00am–5:00pm ET (closed 12–1pm for lunch)",
};

const HELP_ITEMS = [
  {
    icon: CheckCircle2,
    title: "Your tasks",
    body: "Each project lists the items we need from you. Check a task off when you've finished it — that tells us it's ready on our end too.",
  },
  {
    icon: Upload,
    title: "Sending us documents",
    body: "Click the Upload button on any task (or drag files into the upload box inside the task). PDFs, photos, spreadsheets, and QuickBooks files all work. Photos are converted to PDF automatically.",
  },
  {
    icon: MessageSquare,
    title: "Messages",
    body: "Use Messages (top of the page) for general questions, or the message box inside a task for questions about that specific item. We're notified either way.",
  },
  {
    icon: FileText,
    title: "Documents from your firm",
    body: "Files we share with you — finished returns, letters, checklists — appear at the bottom of your home page under \"Documents from Your Firm\" and on the related task.",
  },
];

export default function PortalHelp() {
  const { data: me } = useQuery({
    queryKey: ["portal", "me"],
    queryFn: () => portalApi.me(),
  });

  // tel:/sms:/maps links — on a phone these dial, text, or open navigation directly
  const digits = (s: string) => s.replace(/\D/g, "");
  const contactRows = [
    { icon: Phone, label: "Phone", value: FIRM_CONTACT.phone, href: `tel:+1${digits(FIRM_CONTACT.phone)}` },
    { icon: MessageSquare, label: "Text us", value: FIRM_CONTACT.textNumber, href: `sms:+1${digits(FIRM_CONTACT.textNumber)}` },
    { icon: Phone, label: "Fax", value: FIRM_CONTACT.fax },
    { icon: Mail, label: "Email", value: FIRM_CONTACT.email, href: FIRM_CONTACT.email ? `mailto:${FIRM_CONTACT.email}` : undefined },
    { icon: Globe, label: "Website", value: FIRM_CONTACT.website, href: FIRM_CONTACT.website },
    {
      icon: MapPin,
      label: "Address",
      value: FIRM_CONTACT.address,
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(FIRM_CONTACT.address)}`,
    },
  ].filter((r) => r.value);

  return (
    <PortalLayout accountName={me?.account_name}>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CircleHelp className="w-6 h-6" />
            Help &amp; Contact
          </h1>
          <p className="text-muted-foreground mt-1">How to use your client portal, and how to reach us.</p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Using the portal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {HELP_ITEMS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-3">
                <Icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold">{title}</p>
                  <p className="text-sm text-muted-foreground">{body}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Contact Carpenter &amp; Company, CPAs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {contactRows.map(({ icon: Icon, label, value, href }) => (
              <div key={label} className="flex items-center gap-3 text-sm">
                <Icon className="w-4 h-4 text-primary shrink-0" />
                <span className="font-medium w-20 shrink-0">{label}</span>
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">{value}</a>
                ) : (
                  <span className="truncate">{value}</span>
                )}
              </div>
            ))}
            {FIRM_CONTACT.hours && (
              <p className="text-xs text-muted-foreground pt-1">Office hours: {FIRM_CONTACT.hours}</p>
            )}
            <p className="text-xs text-muted-foreground pt-1">
              The fastest way to reach your team is the <span className="font-medium">Messages</span> button at the top of the page.
            </p>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
